import { localizedStrings } from '@core/strings'
import React, { useEffect, useRef } from 'react'
import {
  useCreateTask,
  useCreateTaskDraft,
  useUpdateTaskDraft,
} from '@core/react-query/features/tasks/task'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { PageUrls } from '@core/page-urls'
import { useRouter } from 'next/router'
import { Text } from '@fundamentals'
import { useModal } from '@common/GlobalModal'
import { ActionButtonProps, StagedNavigationLayout } from '@elements'
import { SaveDraftModal } from '@modules/template-form/modals/SaveDraftModal'
import { hasDirtyFieldsExcept, useInterceptNavigation } from '@common/hooks'
import ConfirmModal from '@common/modal/ConfirmModal'
import {
  TaskForm,
  VirtualizedQuestionGroupListHandle,
} from '@modules/template-form'
import { validateQuestionsStage } from '@elements/Questions/validation/questionsStageGate'
import { CreateTaskFormFields } from './types'
import { useToast } from '@core/toast'
import { TaskTabs } from '@core/entities/task/TaskBase/types'
import { ITaskTemplate } from '@core/entities/task/TaskTemplate'
import { ITemplateQuestionGroup } from '@core/entities/template/TemplateQuestionGroup'
import { ITemplateRule } from '@core/entities/template/TemplateRule'
import { AttachmentTypes } from '@core/components/templateAnswersForm'
import { TaskDraft } from '@core/entities/task/TaskDraft'
import { TaskAssignableUser } from '@core/entities/task/TaskAssignableUser'

type NewTaskPageProps = {
  organisationId: number
  activeProjectId?: number
  projectLevel?: boolean
  draft?: TaskDraft
  initialValues?: Record<string, any>
  onBack?: () => void
  enableDrafts?: boolean
  onSubmit?: (values: CreateTaskFormFields) => void
  disableRouting?: boolean
  disableProjectSelect?: boolean
  disableTaskTemplateSelect?: boolean
  taskTemplates?: Array<ITaskTemplate>
  actionButtons?: Array<ActionButtonProps>
  mainActionButtonLabel?: string
  attachmentTypes?: AttachmentTypes[]
}

const CreateTaskPage: React.FC<NewTaskPageProps> = ({
  organisationId,
  activeProjectId,
  projectLevel,
  draft,
  initialValues,
  onBack,
  enableDrafts = true,
  onSubmit,
  disableRouting,
  disableProjectSelect,
  disableTaskTemplateSelect,
  taskTemplates,
  actionButtons: customActionButtons = [],
  mainActionButtonLabel,
  attachmentTypes,
}) => {
  const router = useRouter()
  const toast = useToast()
  const { showModal, hideModal } = useModal()
  const createTask = useCreateTask()
  const createTaskDraft = useCreateTaskDraft()
  const updateTaskDraft = useUpdateTaskDraft()
  const defaultValues: Partial<CreateTaskFormFields> = draft
    ? {
        ...draft,
        draftId: draft.id,
        assigneeUser: TaskAssignableUser.new(draft?.assigneeUser),
      }
    : {
        ...initialValues,
        projectId: activeProjectId,
      }

  const questionsFormRef = useRef<VirtualizedQuestionGroupListHandle>(null)

  const formContext = useForm<CreateTaskFormFields>({
    mode: 'onChange',
    defaultValues,
  })

  const formSubmitting =
    createTask.isLoading ||
    createTask.isSuccess ||
    createTaskDraft.isLoading ||
    createTaskDraft.isSuccess ||
    updateTaskDraft.isLoading ||
    updateTaskDraft.isSuccess

  const hasUnsavedChanges = hasDirtyFieldsExcept(
    formContext.formState.dirtyFields,
    ['projectId', 'template'],
  )

  useInterceptNavigation({
    canNavigate: !hasUnsavedChanges || formSubmitting,
    onInterceptedNavigation: (navigateToNextRoute, stayOnCurrentRoute) => {
      showModal(
        <ConfirmModal
          title={localizedStrings.unsavedChanges}
          confirmText={localizedStrings.leave}
          onConfirm={() => {
            hideModal()
            navigateToNextRoute()
          }}
          onDismiss={stayOnCurrentRoute}
        >
          {localizedStrings.confirmLeavePage}
        </ConfirmModal>,
      )
    },
  })

  const projectId = formContext.watch('projectId')
  const template = formContext.watch('template')

  useEffect(() => {
    const templateForm = template?.currentVersion
      ?.getCreationStepQuestions()
      ?.toPlain()

    formContext.setValue(
      'questions',
      (templateForm?.questions ?? []) as Array<ITemplateQuestionGroup>,
    )

    formContext.setValue(
      'rules',
      (templateForm?.rules ?? []) as Array<ITemplateRule>,
    )
  }, [template])

  const navigateToTaskDrafts = (toastMessage) => {
    hideModal()
    toast.success(toastMessage)
    if (projectLevel) {
      router.replace(
        PageUrls.projectTasks({
          organisationId,
          projectId,
          query: {
            tab: TaskTabs.DRAFTS,
          },
        }),
      )
    } else {
      router.replace(
        PageUrls.tasks(organisationId, {
          tab: TaskTabs.DRAFTS,
        }),
      )
    }
  }

  const submitTask: SubmitHandler<CreateTaskFormFields> = (values) => {
    // No stage gate on this single-stage form — this is the primary check.
    const canSubmit = validateQuestionsStage({
      formContext,
      questionsFormKey: 'questions',
      scrollToQuestionGroup: (groupIndex) => {
        questionsFormRef.current?.scrollToQuestionGroup(groupIndex)
      },
    })
    if (!canSubmit) {
      return
    }

    const { questions, hiddenQuestionIds, template, assigneeUser, ...rest } =
      values
    const requestBody = {
      ...rest,
      stepAnswers: { answers: questions },
      taskTemplateId: template.currentVersion.templateId,
      taskTemplateVersionId: template.currentVersion.id,
      assigneeUserId: assigneeUser?.id,
    }

    if (onSubmit) {
      onSubmit(values)
      return
    } else {
      createTask.mutate(requestBody, {
        onSuccess: (task) => {
          toast.success(localizedStrings.submitted)

          if (projectLevel) {
            router.replace(
              PageUrls.projectTaskDetail({
                organisationId,
                projectId,
                taskId: task.id,
              }),
            )
          } else {
            router.replace(PageUrls.task(organisationId, task.id))
          }
        },
        onError: (e) => toast.error(e.message),
      })
    }
  }

  const saveDraft = (draftName: string) => {
    const {
      draftId,
      hiddenQuestionIds,
      template,
      questions,
      assigneeUser,
      ...values
    } = formContext.getValues()

    const submitDraft = draftId ? updateTaskDraft : createTaskDraft

    submitDraft.mutate(
      {
        ...values,
        id: draftId,
        organisationId,
        draftName,
        taskTemplateId: template.currentVersion.templateId,
        taskTemplateVersionId: template.currentVersion.id,
        assigneeUserId: assigneeUser?.id,
        stepAnswers: { answers: questions },
      },
      {
        onSuccess: () => {
          navigateToTaskDrafts(localizedStrings.taskDraftSaved)
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  const openSaveDraftModal = () => {
    showModal(
      <SaveDraftModal
        organisationId={organisationId}
        onSaveDraft={saveDraft}
        draftName={draft?.draftName}
      />,
    )
  }

  const stageBuilder = StagedNavigationLayout.StageBuilder()

  stageBuilder.addStage({
    key: 'task',
    actionButtons: [
      {
        variant: 'contained',
        children: mainActionButtonLabel || localizedStrings.submit,
        dataTest: 'submit-task',
        onClick: formContext.handleSubmit(submitTask),
        loading: createTask.isLoading,
      },
    ],
    component: (
      <TaskForm
        ref={questionsFormRef}
        organisationId={organisationId}
        stepId={template?.getNextStepId()}
        disableProjectSelect={disableProjectSelect}
        disableTaskTemplateSelect={disableTaskTemplateSelect}
        taskTemplates={taskTemplates}
        attachmentTypes={attachmentTypes}
      />
    ),
  })

  const stages = stageBuilder.build()

  const actionButtons = [...customActionButtons]

  if (enableDrafts) {
    actionButtons.push({
      variant: 'outlined',
      children: draft
        ? localizedStrings.updateDraft
        : localizedStrings.saveNewDraft,
      onClick: openSaveDraftModal,
      color: 'primary',
      disabled: !projectId || !template,
      dataTest: 'save-draft-btn',
      loading: createTaskDraft.isLoading || updateTaskDraft.isLoading,
    })
  }

  return (
    <FormProvider {...formContext}>
      <StagedNavigationLayout
        disableRouting={disableRouting}
        onBack={onBack}
        stages={stages}
        actionButtons={actionButtons}
        width='90%'
        maxWidth='700px'
        alignSelf='center'
        mt={2}
        ordered
      >
        <Text>{template ? template?.name : localizedStrings.newTask}</Text>
      </StagedNavigationLayout>
    </FormProvider>
  )
}

export { CreateTaskPage }
